import { Web3 } from 'web3';
import AlphaBotABI from '../abi/AlphaBot.json';
import Erc20Abi from '../abi/Erc20Abi.json';
import getFastestWeb3 from '../utils/getFastestWeb3';  
import getFastestWsWeb3 from '../utils/getFastestWsWeb3';
import cachedWeb3Utils from '../utils/cachedWeb3Utils.js';
// Use imported ERC20 ABI
const IERC20_ABI = Erc20Abi;

// RPC节点列表
const RPC_ENDPOINTS = [
  'https://bsc-dataseed.nariox.org',
  'https://bsc-dataseed.defibit.io',
  'https://bsc-dataseed.ninicoin.io',
  'https://bsc.nodereal.io',
  'https://bsc-dataseed-public.bnbchain.org',
  'https://bsc-rpc.publicnode.com',
  'https://rpc-bsc.48.club'
];

// Default configuration values (fallback)
const defaultConfig = {
  contractAddress: '',
  fromTokenAddress: '', // zkj
  toTokenAddress: '', // wbnb
  poolAddress: '', // zkj wbnb pool
  sender: ''
};

export default class SwapService {
  // Singleton instance
  static instance = null;

  // Get the singleton instance
  static getInstance(onLog, onStatusChange, onSwappingChange) {
    if (!SwapService.instance) {
      SwapService.instance = new SwapService(onLog, onStatusChange, onSwappingChange);
    } else if (onLog || onStatusChange || onSwappingChange) {
      // Update callbacks if provided
      if (onLog) SwapService.instance.onLog = onLog;
      if (onStatusChange) SwapService.instance.onStatusChange = onStatusChange;
      if (onSwappingChange) SwapService.instance.onSwappingChange = onSwappingChange;
    }
    return SwapService.instance;
  }

  constructor(onLog, onStatusChange, onSwappingChange) {
    // Callback functions
    this.onLog = onLog || (() => { });
    this.onStatusChange = onStatusChange || (() => { });
    this.onSwappingChange = onSwappingChange || (() => { });
    this.swapLoopId = 1;
    // State variables
    this.state = 'stopped'; // 'stopped' or 'started'
    this.isSwapping = false; // actual running state
    this.web3 = null;
    this.web3Ws = null;
    this.blockSubscription = null;
    this.pendingTxs = new Map(); // txHash -> {promise, nonce}
    this.processedBlocks = new Set();
    this.sendedBlock = new Set();
    this.success = false;
    this.currentNonce = 0;
    this.lastBlockNumber = 0;
    this.pollingInterval = null;
    this.wsPollingInterval = null;
    this.cleanupInProgress = false;
    this.initialBalance = null;
    this.privateKey = null;
    this.blockCounter = 0; // Counter for blocks processed in current round
    this.maxBlocksPerRound = 8; // Maximum number of blocks to process per round
    this.nodeTestPromise = null; // Promise for node testing
    this.txStatus={}; // Record<tx,'send'|'sended'|'success'|'failed'|'timeout'>
    this.selectedRpcNode = 'auto'; // 默认为自动选择
    
    // Initialize Web3 instances based on RPC selection
    // this.initializeWeb3Instances();
    
    // 异步初始化 WebSocket 连接，记录 Promise 以便后续等待
    this.nodeTestPromise = this.initWebSocketConnection().catch(error => {
      this.addLog(`WebSocket节点初始化过程中发生异常: ${error.message}`);

      // 如果初始化失败，回退到默认节点
      const isHttps = window.location.protocol === 'https:';
      const wsProtocol = isHttps ? 'wss' : 'ws';
      const wsPort = isHttps ? '443' : '80';
      const defaultNode = `${wsProtocol}://bsc-ws-node.nariox.org:${wsPort}`;

      this.addLog(`使用默认节点: ${defaultNode}`);
      this.web3Ws = new Web3(defaultNode);
    });
  }

  // 初始化Web3实例
  initializeWeb3Instances() {
    if (this.selectedRpcNode === 'auto') {
      // 使用自动选择最快节点
      this.web3 = new Web3('https://bsc-dataseed.bnbchain.org');
      this.web3Loop = new Web3('https://bsc-dataseed.nariox.org');
      // 使用getFastestWeb3替换
      this.replaceWeb3Promise = (async()=>{
        const {fastest, second} = await getFastestWeb3(this);
        this.web3 = fastest;
        this.web3Loop = second;
      })();
    } else {
      // 使用用户指定的节点
      this.web3 = new Web3(this.selectedRpcNode);
      this.web3Loop = new Web3(this.selectedRpcNode);
      this.replaceWeb3Promise = null; // 不需要自动选择
    }
  }

  // 设置RPC节点
  setRpcNode(rpcNode) {
    this.selectedRpcNode = rpcNode;
    this.addLog(`RPC节点已更新为: ${rpcNode === 'auto' ? '自动选择' : rpcNode}`);
    
    // 如果当前正在运行，先停止
    const wasRunning = this.state === 'started';
    if (wasRunning) {
      this.addLog('检测到正在运行，停止当前操作以更换RPC节点...');
      this.stopContinuousSwap();
    }
    
    // 重新初始化Web3实例
    this.initializeWeb3Instances();
    
    // 如果之前在运行，则重新启动
    if (wasRunning && this.privateKey && this.config) {
      setTimeout(() => {
        this.addLog('RPC节点更换完成，重新启动操作...');
        this.startContinuousSwap(this.privateKey, this.config);
      }, 1000);
    }
  }

  // 初始化WebSocket连接
  async initWebSocketConnection() {
    try {
      this.web3Ws = await getFastestWsWeb3(this);
      return this.web3Ws;
    } catch (error) {
      console.error('WebSocket connection initialization failed:', error);
      throw error;
    }
  }

  // Helper to add logs with timestamps
  addLog(message) {
    const timestamp = new Date().toLocaleTimeString();
    this.onLog(`[${timestamp}] ${message}`);
    console.log(message);
  }

  // Create account from private key
  createAccountFromPrivateKey(web3, privateKey) {
    try {
      // Ensure the private key has the 0x prefix
      const formattedPrivateKey = privateKey.startsWith('0x') ? privateKey : `0x${privateKey}`;
      const account = web3.eth.accounts.privateKeyToAccount(formattedPrivateKey);
      return account;
    } catch (error) {
      console.error('Invalid private key:', error);
      throw new Error('私钥格式无效，请检查后重试');
    }
  }

  // Set state to 'started' and trigger swap loop if not already running
  async startContinuousSwap(privateKey, config = null) {
    // Return if already in 'started' state
    if (this.state === 'started') {
      this.addLog('已处于启动状态，忽略本次启动请求');
      return;
    }

    // Set state to 'started'
    this.state = 'started';
    this.privateKey = privateKey;
    this.config = config || defaultConfig;
    // this.addLog('SwapService状态已设为: started');

    // If not actively swapping, start the swap loop
    if (!this.isSwapping) {
      await this.startSwapLoop();
    }
  }

  // The actual swap loop implementation
  async startSwapLoop() {
    if (this.isSwapping) return;
    this.isSwapping = true;
    this.onSwappingChange(true);
    this.onStatusChange('正在启动连续交换操作...');
    const loopId = this.swapLoopId;
    this.success = false;
    this.pendingTxs.clear();
    this.processedBlocks.clear();
    this.sendedBlock.clear();
    this.lastBlockNumber = 0;
    this.blockCounter = 0; // Reset block counter when starting a new loop
    this.addLog('启动中');
    try {
      // 先等待节点测试完成
      if (this.nodeTestPromise) {
        await this.nodeTestPromise;
      }
      if (this.replaceWeb3Promise) {
        await this.replaceWeb3Promise;
      }

      if (!this.web3Ws) {
        throw new Error('WebSocket连接初始化失败，请刷新页面重试');
      }

      if (!this.privateKey) {
        throw new Error('请先输入私钥');
      }
      // 打印this.web3的rpc和this.web3Ws的rpc
      const httpRpc = this.web3.provider.clientUrl || 'Unknown HTTP RPC';
      const httpRpcLoop = this.web3Loop.provider.clientUrl || 'Unknown HTTP RPC';
      const wsRpc = this.web3Ws.provider._socketPath || 'Unknown WS RPC';
      this.addLog(`当前使用的RPC节点: HTTP=${httpRpc},${httpRpcLoop}, WebSocket=${wsRpc}`);


      // Create account from private key
      const account = this.createAccountFromPrivateKey(this.web3, this.privateKey);
      // this.addLog(`合约启动钱包地址钱包地址: ${account.address}`);

      // Add the account to web3
      this.web3.eth.accounts.wallet.add(account);
      // Use the configuration passed from the component
      const config = this.config;

     

      // Initialize contracts in parallel
      const [contract, toTokenContract] = await Promise.all([
        // Initialize Alpha Bot contract instance
        Promise.resolve(new this.web3.eth.Contract(AlphaBotABI.abi, config.contractAddress)),
        // Initialize token contract
        Promise.resolve(new this.web3.eth.Contract(IERC20_ABI, config.toTokenAddress))
      ]);

      // Get fixed gas amount
      const gasAmount = 400000;

      // Run multiple async operations in parallel using Promise.all
      const [nonce, initialBalance, currentBlockNumber, tokenSymbol] = await Promise.all([
        this.web3.eth.getTransactionCount(account.address),
        toTokenContract.methods.balanceOf(config.sender).call(),
        this.web3Loop.eth.getBlockNumber(),
        cachedWeb3Utils.getCachedTokenSymbol(config.toTokenAddress).catch(() => "Token") // Default to "Token" if symbol() fails
      ]);
      this.processedBlocks.add(currentBlockNumber);
      // Store results
      this.currentNonce = nonce;
      this.initialBalance = BigInt(initialBalance);
      this.toToken = tokenSymbol;

      // Function to execute a transaction for a specific block
      const execTx = async (currentBlockNumber) => {
        if (this.success) {
          return;
        }

        if (this.sendedBlock.has(currentBlockNumber)) {
          return;
        }

        this.sendedBlock.add(currentBlockNumber);

        // Capture current blockCounter in closure
        const currentRound = this.blockCounter; // +1 because we increment after this function is called

        // Use current nonce and increment
        const nonce = this.currentNonce++;
        // this.addLog(`使用nonce: ${nonce}`);

        // Prepare transaction
        const tx = contract.methods.swapTo(
          config.fromTokenAddress,
          config.toTokenAddress,
          config.poolAddress,
          config.sender,
        );

        if (this.success) {
          return;
        }

        // Send transaction but don't wait
        const txPromise = tx.send({
          from: account.address,
          gas: gasAmount,
          maxFeePerGas: this.web3.utils.toWei('0.10051', 'gwei'),
          maxPriorityFeePerGas: this.web3.utils.toWei('0.10051', 'gwei'),
          nonce: nonce
        });
        this.txStatus[txPromise]='send';
        // Store transaction info
        txPromise.on('transactionHash', (hash) => {
          this.txStatus[txPromise]='sended';
          if (!this.success) {
            this.addLog(`交易已发送，哈希: ${hash}`);
          }
          this.pendingTxs.set(hash, { promise: txPromise, nonce: nonce });
        });
        
        // Listen for transaction result
        txPromise.then(async (receipt) => {
          this.success = true;
          this.txStatus[txPromise]='success';
          // Stop polling and subscriptions
          this.cleanupAll();
          this.addLog(`第${currentRound}轮交易成功! 哈希: ${receipt.transactionHash}`);

          if(currentRound<=2){
            this.addLog(`!! 卖出轮次小于等于2 如未显示同区块卖出 下次可以考虑晚一点按下手机上的付款`);
          }

          console.log(receipt);
          // Extract token amount from transaction logs
          if (receipt.logs && receipt.logs.length > 0) {
            try {
              // Get data from the first log (transfer event)
              const logData = receipt.logs[receipt.logs.length-1].data;
              // Convert hex data to BigInt
              const amountInWei = BigInt(logData);
              // Convert from Wei to Ether units (divide by 10^18)
              const amountInEther = Number(amountInWei) / 1e18;
              // Format the number to a reasonable precision
              const formattedAmount = amountInEther.toFixed(6);
              
              // Convert initialBalance from wei to ether
              const initialBalanceInEther = Number(this.initialBalance) / 1e18;
              const curBalance = await toTokenContract.methods.balanceOf(config.sender).call();
              const curBN = Number(BigInt(curBalance))/1e18;
              // Calculate slippage percentage
              const slippagePercentage = (1 - (curBN / initialBalanceInEther)) * 100;
              const formattedSlippage = slippagePercentage.toFixed(4);
              
              // Log the completion message with the amount, token symbol and slippage
              this.addLog(`卖出完成, 获得 ${formattedAmount} 个${this.toToken}, 磨损: ${formattedSlippage}%`);
              
              // Check if there are transactions from config.sender in the same block
              try {
                const blockNumber = receipt.blockNumber;
                const block = await this.web3Loop.eth.getBlock(blockNumber, true);
                if (block && block.transactions) {
                  const senderTxs = block.transactions.filter(tx =>
                    tx.from && tx.from.toLowerCase() === config.sender.toLowerCase()
                  );
                  if (senderTxs.length > 0) {
                    this.addLog(`同区块卖出 ✅`);
                  }
                }
              } catch (error) {
                console.error("Error checking same block transactions:", error);
              }
              
            } catch (error) {
              console.error("Error processing transaction logs:", error);
            }
          }
          
        }).catch(error => {
          this.txStatus[txPromise]='failed';
          if (!this.success) {
            console.log('交易失败',error);
            // If transaction fails, just log error and continue sending new transactions
            this.addLog(`交易失败`);
          }
        });
      };

      // Handle a new block and execute transaction if needed
      this.handleNewBlock = async (blockNumber, source, execTx) => {
        // Skip processed blocks
        if (this.processedBlocks.has(blockNumber) ||
          blockNumber <= this.lastBlockNumber ||
          this.success) {
          return;
        }

        // Update processed blocks set
        this.processedBlocks.add(blockNumber);
        this.lastBlockNumber = blockNumber;

        // Increment block counter
        this.blockCounter++;
        
        this.addLog(`通过${source}检测到新区块 #${blockNumber}，发送交易... (${this.blockCounter}/${this.maxBlocksPerRound})`);
        const counter = this.blockCounter;
        await execTx(blockNumber);
        if (counter === 1) {
          this.addLog(`手机请买入代币!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!`);
        }
        // If we've processed maxBlocksPerRound blocks, stop the operation
        if (this.blockCounter >= this.maxBlocksPerRound) {
          this.addLog(`已处理${this.maxBlocksPerRound}个区块，停止本轮操作`);
          this.success = true; // Set success to true to trigger cleanup
          await this.cleanupAll();
        }
      };

      // Function to cancel pending transactions
      this.cancelPendingTransactions = async () => {
        // Implementation would depend on your cancellation strategy
        // For simplicity, we're just clearing the map without actual cancellation
        // this.addLog('清理所有待处理交易');
        this.pendingTxs.clear();
      };

      // Setup WebSocket polling instead of subscription (to avoid compatibility issues)
      this.wsPollingInterval = setInterval(async () => {
        if (this.success || this.state !== 'started') {
          return;
        }

        try {
          // Use WebSocket connection to get current block number
          const wsBlockNumber = await this.web3Ws.eth.getBlockNumber();

          await this.handleNewBlock(wsBlockNumber, 'WebSocket轮询', execTx);
        } catch (error) {
          console.error('WebSocket polling error:', error);
          // this.addLog(`WebSocket轮询获取新区块时发生错误: ${error.message}`);
        }
      }, 500); // WebSocket polling more frequently, every 200ms

      // this.addLog('已启动WebSocket轮询检测新区块');

      
      // Try to setup WebSocket subscription for new blocks
      try {
        // First wait for subscription creation
        this.blockSubscription = await this.web3Ws.eth.subscribe('newBlockHeaders');
        // this.addLog(`WebSocket订阅成功，ID: ${this.blockSubscription.id}`);

        // Then add event listeners for the subscription
        this.blockSubscription.on('data', async (blockHeader) => {
          try {
            if (this.success || this.state !== 'started') {
              await this.blockSubscription.unsubscribe();
              this.addLog('交易成功或停止中，取消WebSocket区块订阅');
              return;
            }

            // Get complete block info
            const block = await this.web3.eth.getBlock(blockHeader.number, true);
            if (!block || !block.transactions) return;

            // Filter transactions from sender
            const senderTxs = block.transactions.filter(tx =>
              tx.from && tx.from.toLowerCase() === config.sender.toLowerCase()
            );

            // Check status of these transactions
            for (const tx of senderTxs) {
              const receipt = await this.web3.eth.getTransactionReceipt(tx.hash);

              // If transaction failed, execute cleanup
              if (receipt && receipt.status === false) {
                this.addLog(`WebSocket监控: 发现sender地址交易失败: ${tx.hash}`);
                await this.blockSubscription.unsubscribe();
                await this.cleanupAll();
                return;
              } else if (receipt && receipt.status === true) {
                this.addLog(`WebSocket监控: 发现sender地址交易成功: ${tx.hash}`);
                await this.cleanupAll();
              }
            }

            // Calculate block difference
            const blockDiff = blockHeader.number - currentBlockNumber;
            if (blockDiff > this.maxBlockDiff) {
              this.addLog(`已超过${this.maxBlockDiff}个区块监控期，停止WebSocket监控失败交易`);
              await this.blockSubscription.unsubscribe();
              await this.cleanupAll();
            }
          } catch (error) {
            console.error('WebSocket monitoring error:', error);
            // this.addLog(`WebSocket监控失败交易时出错: ${error.message}`);
          }
        });

        this.blockSubscription.on('error', (error) => {
          this.addLog(`WebSocket订阅错误: ${error.message}`);
        });
      } catch (error) {
        console.error('WebSocket subscription creation error:', error);
        // this.addLog(`创建WebSocket订阅时出错: ${error.message}`);
      }
      // Use HTTP polling method as backup
      const getHttpBlock=async () => {
        if (this.success || this.state !== 'started') {
          return;
        }

        try {
          // Get current block number
          const currentBlockNumber = await this.web3Loop.eth.getBlockNumber();

          await this.handleNewBlock(currentBlockNumber, 'HTTP轮询', execTx);
        } catch (error) {
          this.addLog(`HTTP轮询获取新区块时发生错误: ${error.message}`);
        }
      }
      
      this.pollingInterval = setInterval(getHttpBlock, 500); // HTTP polling interval slightly longer, every 500ms
      await getHttpBlock();

      // Execute initial transaction
      // this.addLog(`就绪 开刷!!!!!!!!!!!!!!!!!!!!!!!!!!!!!...${currentBlockNumber}`);
      // await execTx(currentBlockNumber);

    } catch (error) {
      this.addLog(`调用swapTo时发生错误: ${error.message}`);
      this.onStatusChange('错误: ' + error.message);
      this.isSwapping = false;
      this.onSwappingChange(false);
      this.cleanupAll();
    }
  }

  // Stop continuous swap operations - sets state to 'stopped' and cleans up
  async stopContinuousSwap() {
    this.state = 'stopped';
    // this.addLog('SwapService状态已设为: stopped');
    this.onStatusChange('正在停止连续交换操作...');
    await this.cleanupAll();
    this.isSwapping = false;
    this.onSwappingChange(false);
    this.onStatusChange('已停止连续交换操作');
  }

  // Cleanup all resources
  async cleanupAll() {
    this.swapLoopId++;
    if(this.txStatus){
      // 如果所有txStatus都为send 并且key大于4个 说明全部超时了, 要打印log请检查网络状况
      if(Object.values(this.txStatus).every(status => status === 'send') && Object.keys(this.txStatus).length > 4) {
        this.addLog('请检查网络状况 发送交易全部没有上链');
      }
    }
    // Prevent concurrent calls
    if (this.cleanupInProgress) {
      // this.addLog('清理已在进行中，忽略重复调用');
      return;
    }

    this.cleanupInProgress = true;

    try {
      // Clear all intervals
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
        // this.addLog('HTTP区块轮询已停止');
      }

      if (this.wsPollingInterval) {
        clearInterval(this.wsPollingInterval);
        this.wsPollingInterval = null;
        // this.addLog('WebSocket区块轮询已停止');
      }



      // Unsubscribe from WebSocket
      if (this.blockSubscription) {
        try {
          // Add timeout for WebSocket unsubscribe operation (final cleanup)
          await Promise.race([
            this.blockSubscription.unsubscribe(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('WebSocket取消订阅超时')), 3000)
            )
          ]);
        } catch (e) {
          console.error('Error unsubscribing from WebSocket during final cleanup:', e);
          // this.addLog(`最终清理时取消WebSocket订阅出错: ${e.message}`);
        }
        this.blockSubscription = null;
      }

      // Cancel pending transactions
      if (this.cancelPendingTransactions) {
        await this.cancelPendingTransactions();
        // this.addLog('所有其他交易已取消');
      }

      // Clear other references
      this.pendingTxs.clear();
      this.processedBlocks.clear();
      this.sendedBlock.clear();
      this.lastBlockNumber = 0;
      this.blockCounter = 0;
      this.state = 'stopped';

      this.addLog('本轮自动卖出已结束');
      this.isSwapping = false;
      this.onSwappingChange(false);
      this.onStatusChange('已停止交换操作');
    } finally {
      this.cleanupInProgress = false;
    }
  }
} 